/**
 * AI Readiness Page - Shows user's gamification progress and all available tasks
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Circle, Trophy, Target, TrendingUp } from 'lucide-react';
import { useGamification } from '../hooks/useGamification';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_TASKS, TWELVE_MONTH_PACKAGE_TASKS, READINESS_LEVELS } from '../types/gamification';
import { Layout } from '../components/common/Layout';

const AIReadinessPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { progress, loading } = useGamification();

  // Determine which tasks to show based on user tier
  const isTwelveMonthUser = user?.subscriptionTier === 'twelve_month_package';
  const TASKS = isTwelveMonthUser ? TWELVE_MONTH_PACKAGE_TASKS : DEFAULT_TASKS;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your progress...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const completedTasks = progress?.completedTasks || [];
  const totalXP = progress?.totalXP || 0;
  const readinessPercentage = progress?.readinessPercentage || 0;
  const currentLevel = progress?.level || READINESS_LEVELS[0];

  // Group tasks by category
  const tasksByCategory = TASKS.reduce((acc, task) => {
    if (!acc[task.category]) {
      acc[task.category] = [];
    }
    acc[task.category].push(task);
    return acc;
  }, {} as Record<string, typeof TASKS>);

  const categoryNames: Record<string, string> = {
    onboarding: 'Getting Started',
    foundation: 'Building Foundation',
    learning: 'Learning & Growth',
    habit: 'Building Habits',
    mastery: 'Mastery',
    engagement: 'Engagement',
    advocacy: 'Advocacy',
    community: 'Community',
  };

  const handleTaskClick = (taskId: string) => {
    // Navigate to appropriate page based on task
    const routes: Record<string, string> = {
      register_account: '/register',
      complete_profile: '/profile',
      take_skills_assessment: '/skills-assessment',
      complete_first_free_course: '/courses',
      upgrade_account: '/pricing',
      complete_first_paid_course: '/courses',
      complete_5_paid_courses: '/courses',
      complete_3_arts_creative: '/courses',
      complete_3_business_entrepreneur: '/courses',
      complete_3_ai_tools: '/courses',
      login_3_days_streak: '/dashboard',
      login_5_days_streak: '/dashboard',
      finish_10_courses: '/courses',
      become_affiliate: 'https://trainedup.ai/affiliate',
      join_discord: '/community',
    };

    const route = routes[taskId] || '/dashboard';
    
    // Check if it's an external link
    if (route.startsWith('http')) {
      window.open(route, '_blank');
    } else {
      navigate(route);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Your AI Readiness Progress</h1>
          <p className="text-gray-600">
            Track your journey to becoming AI-confident
          </p>
        </div>

        {/* Progress Overview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total XP */}
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Trophy className="w-8 h-8 text-yellow-500 mr-2" />
                <span className="text-3xl font-bold text-gray-900">{totalXP}</span>
              </div>
              <p className="text-sm text-gray-600">Total XP Earned</p>
            </div>

            {/* Readiness Level */}
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <span className="text-2xl mr-2">{currentLevel.emoji}</span>
                <span className="text-3xl font-bold text-gray-900">{readinessPercentage.toFixed(1)}%</span>
              </div>
              <p className="text-sm text-gray-600">{currentLevel.label}</p>
            </div>

            {/* Tasks Completed */}
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Target className="w-8 h-8 text-green-500 mr-2" />
                <span className="text-3xl font-bold text-gray-900">
                  {completedTasks.length}/{TASKS.length}
                </span>
              </div>
              <p className="text-sm text-gray-600">Tasks Completed</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Progress to Expert</span>
              <span className="text-sm text-gray-600">{readinessPercentage.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className={`h-4 rounded-full bg-gradient-to-r ${currentLevel.color} transition-all duration-500`}
                style={{ width: `${readinessPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Tasks by Category */}
        <div className="space-y-6">
          {Object.entries(tasksByCategory).map(([category, tasks]) => (
            <div key={category} className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                {categoryNames[category] || category}
              </h2>
              <div className="space-y-3">
                {tasks.map((task) => {
                  const isCompleted = completedTasks.includes(task.id);
                  return (
                    <div
                      key={task.id}
                      onClick={() => !isCompleted && handleTaskClick(task.id)}
                      className={`flex items-start p-4 rounded-lg border-2 transition-all ${
                        isCompleted
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200 hover:border-blue-300 cursor-pointer hover:shadow-md'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-1">
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div className="ml-4 flex-grow">
                        <div className="flex items-center justify-between">
                          <h3
                            className={`font-semibold ${
                              isCompleted ? 'text-green-900' : 'text-gray-900'
                            }`}
                          >
                            {task.title}
                          </h3>
                          <span
                            className={`text-sm font-medium px-3 py-1 rounded-full ${
                              isCompleted
                                ? 'bg-green-200 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {isCompleted ? '✓ ' : '+'}
                            {task.xpReward} XP
                          </span>
                        </div>
                        <p
                          className={`text-sm mt-1 ${
                            isCompleted ? 'text-green-700' : 'text-gray-600'
                          }`}
                        >
                          {task.description}
                        </p>
                        {isCompleted && (
                          <p className="text-xs text-green-600 mt-2 font-medium">
                            ✓ Completed
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default AIReadinessPage;
